import { defineComponent, reactive, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { withLoading } from '@hems/component';
import { UserReportService, HomeApplianceService } from '@hems/service';
import { getApplianceChartOptions } from '@hems/user/src/containers/homeAppliance/applianceChartOptions';
import { Helper, DateHelper, useLocale } from '@hems/util';
import { CalendarHourly, CalendarDaily, CalendarMonth, GraphChart, EnergyBalance } from '@/components';
import { getDateInitialValue, getNowByClient, divideArray, getEnergyBalance } from '@/containers/report/chartFunctions';
export default defineComponent({
    name: 'ApplianceChartContainer',
    components: {
        CalendarHourly,
        CalendarDaily,
        CalendarMonth,
        GraphChart,
        EnergyBalance,
    },
    props: {
        tabname: {
            type: String,
            required: true,
            default: 'hourly',
        },
    },
    async setup(props) {
        const { t } = useI18n();
        const store = useStore();
        const userReportService = new UserReportService(window.axiosInstance.axios);
        const homeApplianceService = new HomeApplianceService(window.axiosInstance.axios);
        const state = reactive({
            show: false,
            tabname: props.tabname,
            date: null,
            energyBalance: [],
            data: {
                heatPumpData: [],
                airConditionerData: [],
                smartPlugData: [],
                tvData: [],
            },
        });
        const { intlFormatLocale } = useLocale();
        const baseDeviceInfo = computed(() => store.state.device.baseDeviceInfo ?? null);
        const siteId = computed(() => Number(store.state.user.homeId));
        watch(() => props.tabname, (tabname) => {
            state.tabname = tabname;
        });
        watch(() => state.tabname, () => {
            state.show = false;
            loadData();
        });
        watch(() => state.date, () => {
            if (!state.date)
                return;
            loadData();
        }, {
            deep: true,
        });
        watch(() => intlFormatLocale.value, () => {
            rerenderData();
        });
        function toDateFormatString(date, interval) {
            const year = date.getFullYear();
            const month = Helper.padLeft(date.getMonth() + 1, 2);
            const day = Helper.padLeft(date.getDate(), 2);
            return interval === 'monthly' ? [year, month].join('') : [year, month, day].join('');
        }
        function getDateParams() {
            if (state.tabname === 'hourly') {
                return {
                    from: `${toDateFormatString(state.date?.hourly, state.tabname)}00`,
                    to: `${toDateFormatString(state.date?.hourly, state.tabname)}23`,
                };
            }
            if (state.tabname === 'daily') {
                return {
                    from: toDateFormatString(state.date?.daily.start, state.tabname),
                    to: toDateFormatString(state.date?.daily.end, state.tabname),
                };
            }
            return {
                from: toDateFormatString(state.date?.monthly.start, state.tabname),
                to: toDateFormatString(state.date?.monthly.end, state.tabname),
            };
        }
        const options = computed(() => getApplianceChartOptions(state.tabname, intlFormatLocale.value, state.data, t));
        function createChartData(data) {
            state.data.heatPumpData = data.HeatPump?.map((item) => ({
                x: DateHelper.getTime(item.colec_dt, state.tabname),
                y: item.gen || 0,
            }));
            state.data.airConditionerData = data['Air Conditioner']?.map((item) => ({
                x: DateHelper.getTime(item.colec_dt, state.tabname),
                y: item.gen || 0,
            }));
            state.data.smartPlugData = data['Smart Plug']?.map((item) => ({
                x: DateHelper.getTime(item.colec_dt, state.tabname),
                y: item.gen || 0,
            }));
            state.data.tvData = data.TV?.map((item) => ({
                x: DateHelper.getTime(item.colec_dt, state.tabname),
                y: item.gen || 0,
            }));
            const heatPumpSum = state.data.heatPumpData?.map((item) => item.y ?? 0).reduce((prev, curr) => prev + curr);
            const airConditionerSum = state.data.airConditionerData
                ?.map((item) => item.y ?? 0)
                .reduce((prev, curr) => prev + curr);
            const smartPlugSum = state.data.smartPlugData?.map((item) => item.y ?? 0).reduce((prev, curr) => prev + curr);
            const tvSum = state.data.tvData?.map((item) => item.y ?? 0).reduce((prev, curr) => prev + curr);
            state.energyBalance = divideArray([
                { value: heatPumpSum || 0, unit: 'kWh', type: 'Heat Pump' },
                { value: airConditionerSum || 0, unit: 'KWh', type: 'Air Conditioner' },
                { value: smartPlugSum || 0, unit: 'KWh', type: 'Smart Plug' },
                { value: tvSum || 0, unit: 'KWh', type: 'TV' },
            ], 2);
        }
        const getServerTime = () => {
            userReportService
                .getServerTime(baseDeviceInfo.value?.timezone_id ?? '')
                .then(async (res) => {
                state.date = getDateInitialValue(res);
            })
                .catch(async (e) => {
                console.error(e);
                state.date = getDateInitialValue(getNowByClient(baseDeviceInfo.value?.timezone_id ?? ''));
            });
        };
        async function loadData() {
            if (state.date === null)
                return;
            await withLoading(async () => {
                await homeApplianceService
                    .getApplianceChart(siteId.value, {
                    interval: state.tabname,
                    ...getDateParams(),
                    timezoneId: baseDeviceInfo.value?.timezone_id ?? '',
                })
                    .then((res) => {
                    createChartData(res);
                })
                    .catch((e) => {
                    console.error(e);
                    state.energyBalance = [[], [], [], [], []];
                    state.data.heatPumpData = [];
                    state.data.airConditionerData = [];
                    state.data.smartPlugData = [];
                    state.data.tvData = [];
                });
            })();
            nextTick(() => (state.show = true));
        }
        getServerTime();
        function rerenderData() {
            state.show = false;
            nextTick(() => (state.show = true));
        }
        return {
            state,
            options,
            energyBalance: computed(() => getEnergyBalance(state.energyBalance, intlFormatLocale.value, t)),
        };
    },
});
